import type { AgentTaskModel } from "@fencyai/js";
import { useAgentTasks } from "@fencyai/react";
import { useCallback, useState } from "react";

type Props = {
  model: AgentTaskModel;
  fetchClientToken: () => Promise<{ clientToken: string }>;
};

const IMPROVE_SCHEMA = JSON.stringify({
  type: "object",
  additionalProperties: false,
  properties: {
    improvedDescription: {
      type: "string",
      description: "The improved description as simple HTML using <p>, <ul>, <li> and <strong> tags only.",
    },
  },
  required: ["improvedDescription"],
});

export const useImproveNeedDescription = ({ model, fetchClientToken }: Props) => {
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);

  const fetchToken = useCallback(
    async (taskType: "StreamingChatCompletion" | "StructuredChatCompletion" | "MemoryChat" | "MemorySearch") => {
      void taskType;
      return fetchClientToken();
    },
    [fetchClientToken],
  );

  const { createAgentTask } = useAgentTasks({ fetchCreateAgentTaskClientToken: fetchToken });

  const improve = useCallback(
    async (title: string, description: string): Promise<string | null> => {
      setIsLoading(true);
      setHasError(false);
      try {
        const result = await createAgentTask({
          type: "StructuredChatCompletion",
          model,
          jsonSchema: IMPROVE_SCHEMA,
          messages: buildMessages(title, description),
        });
        if (result.type !== "success" || result.response.taskType !== "StructuredChatCompletion") {
          setHasError(true);
          return null;
        }
        const improved = parseImproved(result.response.response.jsonResponse);
        if (improved === null) setHasError(true);
        return improved;
      } catch {
        setHasError(true);
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [createAgentTask, model],
  );

  return { improve, isLoading, hasError };
};

const buildMessages = (title: string, description: string) => [
  {
    role: "SYSTEM" as const,
    content: [
      "You are a procurement adviser for a Norwegian public-sector organisation.",
      "Improve and expand the procurement need description provided by the user.",
      "Write in Norwegian bokmål. Be concrete, precise and professional, and use bullet points where it helps.",
      "Keep all relevant information from the original. Do not invent information you have no basis for.",
    ].join(" "),
  },
  {
    role: "USER" as const,
    content: [`Title: ${title || "(no title)"}`, `Current description:\n${description || "(no description)"}`].join(
      "\n\n",
    ),
  },
];

const parseImproved = (json: string): string | null => {
  try {
    const value = (JSON.parse(json) as { improvedDescription?: unknown }).improvedDescription;
    return typeof value === "string" && value.trim() !== "" ? value : null;
  } catch {
    return null;
  }
};
